// The table's live state for every surface (slice 06; before this it lived
// inside the placeholder board): the projected store, moved forward by the
// socket's envelopes; the DM-away status; the way to act, with every refusal
// said in words; and the last MOVE that arrived, with its route, so the
// board can animate the token along it for everyone (design/12 §4). The
// board draws from it, the properties panel lists from it — one store, so
// the surfaces cannot disagree (design/19 §2).
//
// Looking through a player's eyes (slice 09; design/11 §6, design/22 §8):
// while the DM views as somebody, every surface draws from THAT person's
// projection, fetched from the server — the payload the person gets, never
// a client-side imitation — and re-fetched as the table moves. The DM's own
// store keeps moving underneath, so coming back is instant.
import { takebacks } from '../takeback';
import { trackAct } from '../analytics';
import { nudgeInitiative, nudgeTurn, promptCame, turnCame } from '../turnNudge';
import { boardBus } from '../board/bus';
import { selection } from '../board/selection';
import { sheetPulse } from '../sheetPulse';
import { floorOffer, viewedBase, viewedFloor } from '../board/floorView';
import { effectsBus, effectsFor, noteSent } from '../board/effects';
import { dice3dBus } from '../board/DiceTray';
import { throwFromFrame } from '../board/dice3d';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Socket } from 'socket.io-client';
import {
  type Combatant, type LinkPair, defaultRules, parseManeuverOffer, parseRulesView, type RulesView, emptyState, followMoves, projectEvent, type TableRules, type TableState, NOT_YOUR_TURN, TABLE_ACT, TABLE_PING, type Ping, TABLE_EVENT, TABLE_LIVE_CHANGED, TABLE_NOTICE, TABLE_SHEET, TABLE_RULES, TABLE_STATUS, TABLE_SWITCH, TABLE_SYNC, TABLE_VIEW_SCENE, TURN_ENDED, parseStatus, type ActReply, type EventBody, type Point, type Role, type Snapshot, type TableStatus,
  foldAnswer, foldSave, isRulingBody, reduceRulings, type PlayerRuling, type Ruling, seesAsDm, type Power,
  emptyAudio, normaliseAudio, type AudioState, type Floor,
} from '@dragons-table/vtt-shared';
import { emptyStore, fromSnapshot, receive, type Store } from '../store';
import { fetchViewAs } from '../table';
import { fetchLog, type LogEntry } from '../dice';
import { foldAttackAc } from './attackFold';
import { foldAsks, type Asks } from '../asks';
import { foldHandouts, handoutsAs, type Handouts } from '../handouts';
import { foldDecks, type Decks } from '../cards';
import { foldAudio } from '../sound';
import { dialogs } from '../dialog';
import { VIEW_ONLY_REASON } from './viewOnly';
import { toggleWire } from '../board/toggleBoxes';

export interface Travel {
  /** Every thing that set off together: one for a move, several for a group move (issue 830). */
  moves: Array<{ id: string; route: Point[] }>;
  /** The envelope's counter, so two moves of the same thing are two travels. */
  n: number;
}

/** What acting under View as says (issue 1371): nothing is done through somebody else's eyes. */
export const lookingThrough = (who: ViewingAs): string =>
  `You are looking through ${who.label ?? (who.handle ? `@${who.handle}` : 'somebody')}'s eyes. Back to your view to change the table.`;

export interface ViewingAs {
  id: string;
  handle: string | null;
  /** How the table names them (issue 1023), fixed when the DM picks them; absent reads as their handle. */
  label?: string;
  role: Role;
}

/**
 * The role a person's board and store run as (issue 1159): a co-DM's is the DM's with `see_hidden` — they are sent the
 * DM's frames — and a player's without it (a player who owns nothing); anyone else's is their own.
 */
export function boardRole(role: Role, powers?: readonly Power[] | null): Role {
  if (role !== 'codm') return role;
  return seesAsDm({ role, powers: powers ?? [] }) ? 'dm' : 'player';
}

/**
 * A button on a notice: one that opens one of the table's tools (`boardBus.showTool`), or one that does what the notice
 * offers and puts it away (issue 1537: "Goblin is still in the turn order." with Remove from turn order).
 */
export type NoticeOpen = { label: string; tool: string } | { label: string; run: () => void };

export interface TableStateValue {
  /** A co-DM's powers (issue 1159), or null for anyone else: the tools and acts they offer follow them. */
  powers: Power[] | null;
  store: Store;
  status: TableStatus;
  notice: string;
  /** Counts every notice set (issue 1175), so the same words said twice are announced twice. */
  noticeId: number;
  /** A tool the notice offers to open (issue: "Open Handouts to read it" had no button), or null. */
  noticeOpen: NoticeOpen | null;
  connected: boolean;
  /** The role the store is projected for: the viewer's own, or the person being viewed as. */
  role: Role;
  meId: string;
  /** The move that last arrived, for the board to play out. */
  travel: Travel | null;
  act: (body: EventBody, onReply?: (reply: ActReply) => void) => void;
  setNotice: (text: string, open?: NoticeOpen) => void;
  /** Whose eyes the DM is looking through, or null for their own. */
  viewingAs: ViewingAs | null;
  /** The DM alone: look as a person at the table, or (null) as themselves again. */
  viewAs: (who: ViewingAs | null) => void;
  /** The table's roll log as this person may see it (slice 11): oldest first, moved forward by the events. */
  log: LogEntry[];
  /** Counts the log's whole-page loads (issue 1175): what a load brings is history, never announced as new. */
  logLoads: number;
  logMore: boolean;
  loadOlderLog: () => Promise<void>;
  /** The open group roll requests this person was given (issue 1049): the DM's with progress, a player's own prompts. */
  asks: Asks;
  /** The table's rules settings (issue 1125): the DM's, read with the log; a player's are the defaults, unused. */
  rules: TableRules;
  /**
   * The DM's rulings in force (issue 1136), read with the log and moved by the events: the DM's whole (`Ruling`), a
   * player's as they read them (`PlayerRuling`: the words, whether kept, a bonus's numbers).
   */
  rulings: Array<Ruling | PlayerRuling>;
  /** Staged handouts (issue 1154), read with the log and moved by the events: the DM's every one, a player's only those sent to them. */
  handouts: Handouts;
  /** Card decks in play (issue 1177), as this person was sent them: their own hand whole, others' counts. Read with the log, moved by the frames. */
  decks: Decks;
  /** What the table is playing (issue 1161): from the snapshot, moved by the events; the same for everyone at the table. */
  audio: AudioState;
  /** This screen's clock minus the server's, in ms, as last seen on a sound's start: how a track is put in step. */
  clockSkew: number;
  /**
   * What this screen shows by the rules of the table's game system (issue 1303), sent with the snapshot: whether an
   * attack can be made and its form's choices, the ruler's unit, the effect units and bonus types. Never fetched here.
   */
  systemRules: RulesView;
  /** The DM's alone (issue 834): the scene on this screen, and whether the players are on it. Null for a player. */
  scene: { id: string; live: boolean } | null;
  /** The DM looks at another scene (staging): their own screen only. */
  viewScene: (sceneId: string) => void;
  /** A quick ping at a point on the board (issue 1039): everyone on this scene sees it fade; nothing is stored. */
  ping: (x: number, y: number) => void;
}

/** The latest start in the table's sound, ms on the server's clock (0 for silence). */
const newestStart = (a: AudioState): number => Math.max(0, ...[a.music?.startAt, a.ambience?.startAt, ...a.effects.map((e) => e.startAt)].filter((x): x is string => !!x).map(Date.parse));

const Ctx = createContext<TableStateValue | null>(null);

/** The table's state where there may be none (a panel mounted on its own): null outside the table. */
export function useOptionalTableState(): TableStateValue | null {
  return useContext(Ctx);
}

/** A new thing, area or wall placed on the floor in view, standing on it (qa-runs#1160). Unchanged with one floor. */
function onViewedFloor(body: EventBody, state: { scene: { floors?: Floor[] }; objects: Record<string, { floor?: string; owners?: readonly string[]; mine?: boolean }> }, meId: string, isDm: boolean): EventBody {
  const floor = viewedFloor(state, meId, isDm);
  if (!floor) return body;
  if (body.kind === 'object.add' && body.object.floor === undefined) return { ...body, object: { ...body.object, floor } };
  if (body.kind === 'area.set' && body.area.floor === undefined) { const z = viewedBase(state.scene.floors, floor); return { ...body, area: { ...body.area, floor, ...(z ? { z } : {}) } }; }
  if (body.kind === 'wall.add') return { ...body, walls: body.walls.map((w) => (w.floor === undefined ? { ...w, floor } : w)) };
  return body;
}

export function useTableState(): TableStateValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTableState outside the table');
  return v;
}

/** Whose eyes the DM is looking through, for chrome that may render outside a table (a tool's body in a test): null there. */
export function useViewing(): Pick<TableStateValue, 'viewingAs' | 'viewAs'> | null {
  return useContext(Ctx);
}


/** How long a board notice stays before it fades. */
export const NOTICE_MS = 10_000;
/**
 * A notice with a button on it ("Open Cards") stays a minute, and has its own ×: one that faded in ten seconds was gone
 * before a slower hand reached the button, and the click fell on the board (qa-runs #125; WCAG 2.2.1).
 */
export const NOTICE_ACTION_MS = 60_000;

/**
 * How long an act waits for the table's answer. An act is one write and one broadcast, answered well inside a second;
 * a socket whose connection died without a close only notices after its heartbeat lapses (up to 45 s), and an act sent
 * into that gap is never answered. Ten seconds leaves a slow phone connection room and the person not waiting on nothing.
 */
export const ACT_TIMEOUT_MS = 10_000;
/** Said, and given as the act's reason, when the answer did not come in time. The act may still have landed, so the board is the check. */
export const ACT_TIMED_OUT = 'That did not reach the table in time. If the board does not show it, try again.';
export const ACT_DISCONNECTED = 'You are disconnected; nothing you do now is sent.';

export function TableStateProvider({ socket, campaignId, role, powers = null, meId, snapshot, connected, children }: {
  socket: Socket; campaignId: string; role: Role; powers?: Power[] | null; meId: string; snapshot: Snapshot | null; connected: boolean; children: ReactNode;
}) {
  const [store, setStore] = useState<Store>(() => (snapshot ? fromSnapshot(snapshot.state, snapshot.n) : emptyStore()));
  const [status, setStatus] = useState<TableStatus>(snapshot?.status ?? { dmPresent: false, viewOnly: true });
  // A notice is a passing message — a refusal, "Ogre is yours now.", "The scene changed." — and fades on its own after
  // NOTICE_MS; an ongoing state (disconnected, the DM away) is the status pill's, not a notice. Each notice has its
  // own id, so the same words said again get their full time (found by the QA agent: it never faded).
  const [noticeAt, setNoticeAt] = useState<{ text: string; id: number; open?: NoticeOpen }>({ text: '', id: 0 });
  const notice = noticeAt.text;
  const setNotice = useCallback((text: string, open?: NoticeOpen) => setNoticeAt((cur) => ({ text, id: cur.id + 1, ...(open ? { open } : {}) })), []);
  useEffect(() => {
    if (!noticeAt.text) return undefined;
    const t = setTimeout(() => setNoticeAt((cur) => (cur.id === noticeAt.id ? { text: '', id: cur.id } : cur)), noticeAt.open ? NOTICE_ACTION_MS : NOTICE_MS);
    return () => clearTimeout(t);
  }, [noticeAt]);
  const [travel, setTravel] = useState<Travel | null>(null);
  const [viewingAs, setViewingAs] = useState<ViewingAs | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);
  // The log as last drawn, for a handler to compare an updated roll with the one it replaces.
  const logNow = useRef<LogEntry[]>(log);
  logNow.current = log;
  const [logCursor, setLogCursor] = useState<string | null>(null);
  const [logMore, setLogMore] = useState(false);
  const [logLoads, setLogLoads] = useState(0);
  const [asks, setAsks] = useState<Asks>({});
  const [rules, setRules] = useState<TableRules>(defaultRules());
  const [rulings, setRulings] = useState<Array<Ruling | PlayerRuling>>([]);
  const [handouts, setHandouts] = useState<Handouts>({});
  const [decks, setDecks] = useState<Decks>({});
  const [audio, setAudio] = useState<AudioState>(() => normaliseAudio(snapshot?.audio ?? emptyAudio()));
  const [clockSkew, setClockSkew] = useState(0);
  /** A snapshot's sound (the table's own, the same on every scene): kept unless a server older than issue 1161 sent none. */
  const takeAudio = useCallback((snap: Snapshot) => { if (snap.audio !== undefined) setAudio(normaliseAudio(snap.audio)); }, []);
  const [systemRules, setSystemRules] = useState<RulesView>(() => parseRulesView(snapshot?.systemRules));
  /** A snapshot's system rules (the same on every scene): kept unless a server older than issue 1303 sent none. */
  const takeRules = useCallback((snap: Snapshot) => { if (snap.systemRules !== undefined) setSystemRules(parseRulesView(snap.systemRules)); }, []);
  const [peek, setPeek] = useState<Store | null>(null);
  const [scene, setScene] = useState<{ id: string; live: boolean } | null>(snapshot?.scene ?? null);
  const storeRef = useRef(store);
  storeRef.current = store;
  const peekRef = useRef<ViewingAs | null>(null);
  // The latest act, for a notice's button made before it (a maneuver's try-back, qa-runs#398).
  const actRef = useRef<((body: EventBody, onReply?: (reply: ActReply) => void) => void) | null>(null);
  const peekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The log's latest page, on arrival and again after a gap (an envelope missed may have been a roll).
  const loadLog = useCallback(async () => {
    const page = await fetchLog(campaignId);
    setLog(page.entries); setLogCursor(page.cursor); setLogMore(page.more); setLogLoads((n) => n + 1);
    if (page.asks) setAsks(Object.fromEntries(page.asks.map((a) => [a.id, a])));
    if (page.rules) setRules(page.rules);
    if (page.rulings) setRulings(page.rulings);
    if (page.handouts) setHandouts(page.handouts);
    if (page.decks) setDecks(page.decks);
  }, [campaignId]);
  useEffect(() => { void loadLog(); }, [loadLog]);
  const loadOlderLog = useCallback(async () => {
    if (logCursor === null) return;
    const page = await fetchLog(campaignId, logCursor);
    setLog((cur) => [...page.entries, ...cur]); setLogCursor(page.cursor); setLogMore(page.more);
  }, [campaignId, logCursor]);

  const resync = useCallback(() => {
    socket.emit(TABLE_SYNC, { campaignId }, (snap: Snapshot | { error: string }) => {
      if ('state' in snap) { setStore(fromSnapshot(snap.state, snap.n)); setStatus(snap.status); setScene(snap.scene ?? null); takeRules(snap); takeAudio(snap); void loadLog(); }
    });
  }, [socket, campaignId, loadLog, takeRules, takeAudio]);

  // The viewed person's projection: fetched now, and again a beat after anything moves.
  const refetchPeek = useCallback(() => {
    const who = peekRef.current;
    if (!who) return;
    if (peekTimer.current) clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(async () => {
      peekTimer.current = null;
      const got = await fetchViewAs(campaignId, who.id);
      if (peekRef.current?.id !== who.id) return;
      if (got) setPeek(fromSnapshot(got.state, 0));
      else { setNotice(`${who.label ?? (who.handle ? `@${who.handle}` : 'That person')} is no longer at the table.`); peekRef.current = null; setViewingAs(null); setPeek(null); }
    }, 120);
  }, [campaignId]);

  const viewAs = useCallback((who: ViewingAs | null) => {
    peekRef.current = who;
    setViewingAs(who);
    setPeek(null);
    setNotice('');
    if (who) refetchPeek();
  }, [refetchPeek]);

  useEffect(() => {
    if (snapshot) { setStore(fromSnapshot(snapshot.state, snapshot.n)); setStatus(snapshot.status); setScene(snapshot.scene ?? null); takeRules(snapshot); takeAudio(snapshot); }
  }, [snapshot, takeRules, takeAudio]);
  // NPC dialog (qa-runs#1167): the lines this person heard, read on arrival and again with each fresh snapshot.
  useEffect(() => { if (snapshot) dialogs.attach(socket, campaignId); }, [snapshot, socket, campaignId]);
  // A player's Take back move (issue 1436): the DM's requests waiting, read on arrival and with each fresh snapshot.
  useEffect(() => { if (snapshot) takebacks.attach(socket, campaignId, role === 'dm'); }, [snapshot, socket, campaignId, role]);
  // The hot bar's Toggle boxes (qa-runs#371) ask the server on this socket.
  useEffect(() => { toggleWire.attach(socket); }, [socket]);

  useEffect(() => {
    const onEvent = (raw: unknown) => {
      const before = storeRef.current;
      const r = receive(before, raw, role, meId);
      if (r.gap) { resync(); return; }
      // Two envelopes can land before React renders: the ref moves NOW, or the second reads as a gap.
      storeRef.current = r.store;
      setStore(r.store);
      refetchPeek();
      const ev = r.event;
      if (!ev) return;
      // NPC dialog (qa-runs#1167): a line heard (its bubble), or one deleted.
      dialogs.take(ev);
      // Visual effects (issue 1164): from this frame and this store alone; none while looking through somebody's eyes.
      if (!peekRef.current) effectsBus.play(effectsFor(ev, before.state, r.store.state, meId));
      // 3D dice (issue 1183): a roll this person was sent, with its faces — the log line is the log's, at once, either way.
      if (!peekRef.current) dice3dBus.play(throwFromFrame(ev));
      // The group roll requests (issue 1049), moved by what this person was sent.
      setAsks((cur) => foldAsks(cur, ev, meId));
      // Staged handouts (issue 1154): a player is told, briefly, when one arrives or changes.
      setHandouts((cur) => foldHandouts(cur, ev));
      // Cards (issue 1177): the deck as this person now sees it; a card shown from someone's hand is said once.
      setDecks((cur) => foldDecks(cur, ev));
      // The table's sound (issue 1161): a start stamped just now by the server says how far this screen's clock is off.
      setAudio((cur) => {
        const next = foldAudio(cur, ev);
        const newest = newestStart(next);
        if (next !== cur && newest > newestStart(cur) && Math.abs(Date.now() - newest) < 60_000) setClockSkew(Date.now() - newest);
        return next;
      });
      if (ev.kind === 'card.revealed') setNotice(`Shown from a hand: ${ev.card.label}.`);
      // A card that came into this person's hand by someone else's act (issue 1451), told as a handout is, with the button
      // that opens Cards: never which card.
      if (ev.kind === 'deck' && ev.toYou) {
        const n = ev.toYou.count === 1 ? 'a card' : `${ev.toYou.count} cards`;
        setNotice(ev.toYou.how === 'dealt' ? `You were dealt ${n}.` : 'A card was passed to you.', { label: 'Open Cards', tool: 'cards' });
      }
      // With the button that opens Handouts on it: the words alone sent the player hunting the menus for the panel.
      if (ev.kind === 'handout.set') setNotice(`A handout: ${ev.handout.title}.`, { label: 'Open Handouts', tool: 'handouts' });
      // A rewind (issue 1169): the board came back whole in the frame; the log is read again, so every roll made in the
      // minutes it took back carries its mark. Everyone is told, in a word.
      if (ev.kind === 'table.rewind') { void loadLog(); setNotice(ev.undo ? 'The DM undid their last change.' : 'The DM rewound the table. Rolls made since stay in the log, marked.'); refetchPeek(); }
      // The log (slice 11): a roll a player may see, the DM's own event, a cleared view, an amendment folded into its roll.
      // A roll already shown is its new version (issue 1529: a Karma use on a public test), by its id.
      if (ev.kind === 'roll') {
        const roll = ev.roll;
        // The DM dropped what this person applied (qa-runs#1400): a notice as well as the log's line, once, as it happens.
        const was = logNow.current.find((e) => e.kind === 'roll' && e.roll.id === roll.id);
        const wasDropped = (t: string) => was?.kind === 'roll' && was.roll.applied?.some((a) => a.target === t && a.settled?.amount === null);
        const dropped = was ? roll.applied?.find((a) => a.by.id === meId && a.settled && a.settled.amount === null && !wasDropped(a.target)) : undefined;
        if (dropped) setNotice(`The DM dropped the ${dropped.amount} ${dropped.heal ? 'healing' : 'damage'} you applied to ${dropped.name}.`);
        setLog((cur) => (cur.some((e) => e.kind === 'roll' && e.roll.id === roll.id) ? cur.map((e) => (e.kind === 'roll' && e.roll.id === roll.id ? { kind: 'roll', roll } : e)) : [...cur, { kind: 'roll', roll }]));
      }
      else if (ev.kind === 'log.clear') setLog((cur) => [...cur, { kind: 'clear', at: ev.at, actorId: ev.actorId }]);
      else if (ev.kind === 'dm.event' && ev.body.kind === 'roll') { const roll = ev.body.roll; setLog((cur) => [...cur, { kind: 'roll', roll }]); }
      else if (ev.kind === 'dm.event' && ev.body.kind === 'log.clear') setLog((cur) => [...cur, { kind: 'clear', at: ev.at, actorId: ev.actorId }]);
      // A focus note posted to the log (slice 13, decision 23): everyone's, from the event that set it.
      else if (ev.kind === 'focus.set' && ev.focus.posted && ev.focus.note) { const { note } = ev.focus; const at = ev.at; const actorId = ev.focus.ownerId; setLog((cur) => [...cur, { kind: 'note', note, at, actorId }]); }
      // Since issue 834 a posted note arrives as its own table-wide event (the focus itself stays on its scene).
      else if (ev.kind === 'log.note') { const { note, ownerId, at, announce } = ev; setLog((cur) => [...cur, { kind: 'note', note, at, actorId: ownerId, ...(announce ? { announce } : {}) }]); }
      else if (ev.kind === 'dm.event' && ev.body.kind === 'log.note') { const { note, ownerId, rule, announce } = ev.body; const at = ev.at; setLog((cur) => [...cur, { kind: 'note', note, at, actorId: ownerId, ...(rule ? { rule } : {}), ...(announce ? { announce } : {}) }]); }
      else if (ev.kind === 'dm.event' && ev.body.kind === 'focus.set' && ev.body.focus.posted && ev.body.focus.note) { const { note, ownerId } = ev.body.focus; const at = ev.at; setLog((cur) => [...cur, { kind: 'note', note, at, actorId: ownerId }]); }
      // An attack as this player may know it (issue 1124): a new one, or the new version of one already shown.
      else if (ev.kind === 'attack') { const attack = ev.attack; setLog((cur) => (cur.some((e) => e.kind === 'attack' && e.attack.id === attack.id) ? cur.map((e) => (e.kind === 'attack' && e.attack.id === attack.id ? { kind: 'attack', attack } : e)) : [...cur, { kind: 'attack', attack }])); }
      // A template's saves as this player may know them (issue 1129): new, or the new version of one already shown.
      // A trigger's chain as this player may know it (issue 1500): new, or the new version of one already shown.
      else if (ev.kind === 'trigger') { const trigger = ev.trigger; setLog((cur) => (cur.some((e) => e.kind === 'trigger' && e.trigger.id === trigger.id) ? cur.map((e) => (e.kind === 'trigger' && e.trigger.id === trigger.id ? { kind: 'trigger', trigger } : e)) : [...cur, { kind: 'trigger', trigger }])); }
      // The DM's: a trigger's answer or its damage settled is the record as it now stands.
      else if (ev.kind === 'dm.event' && (ev.body.kind === 'attack.answer' || ev.body.kind === 'save.apply' || ev.body.kind === 'save.rethrow') && ev.body.record?.trigger) { const { rollId, record } = ev.body; setLog((cur) => cur.map((e) => (e.kind === 'roll' && e.roll.id === rollId ? { kind: 'roll', roll: { ...e.roll, trigger: record.trigger! } } : e))); }
      else if (ev.kind === 'save') { const save = ev.save; setLog((cur) => (cur.some((e) => e.kind === 'save' && e.save.id === save.id) ? cur.map((e) => (e.kind === 'save' && e.save.id === save.id ? { kind: 'save', save } : e)) : [...cur, { kind: 'save', save }])); }
      // The DM's: a creature's bonus supplied, or its damage settled, folded into the resolve.
      else if (ev.kind === 'dm.event' && (ev.body.kind === 'save.bonus' || ev.body.kind === 'save.apply' || ev.body.kind === 'save.rethrow')) { const body = ev.body; setLog((cur) => cur.map((e) => (e.kind === 'roll' && e.roll.id === body.rollId && e.roll.save ? { kind: 'roll', roll: foldSave(e.roll, body) } : e))); }
      else if (ev.kind === 'dm.event' && ev.body.kind === 'table.rules') { const next = ev.body.rules; setRules(next); }
      // The DM's rulings (issue 1136): the DM folds each event over the whole list; a player is sent the list as it stands.
      else if (ev.kind === 'dm.event' && isRulingBody(ev.body)) { const body = ev.body; setRulings((cur) => reduceRulings(cur as Ruling[], body)); }
      else if (ev.kind === 'rulings') setRulings(ev.rulings);
      else if (ev.kind === 'dm.event' && ev.body.kind === 'attack.apply') { const { rollId, amount } = ev.body; const auto = ev.body.auto === true; const leftover = ev.body.leftover === true; setLog((cur) => cur.map((e) => (e.kind === 'roll' && e.roll.id === rollId && e.roll.attack ? { kind: 'roll', roll: { ...e.roll, attack: { ...e.roll.attack, settled: { amount, auto, ...(leftover ? { leftover: true as const } : {}) } } } } : e))); }
      // An answer to an attack's house rule (issue 1499): the record the server stamped is the attack as it now stands.
      else if (ev.kind === 'dm.event' && ev.body.kind === 'attack.answer' && ev.body.record?.attack) { const { rollId, record } = ev.body; setLog((cur) => cur.map((e) => (e.kind === 'roll' && e.roll.id === rollId && e.roll.attack ? { kind: 'roll', roll: foldAnswer(e.roll, record) } : e))); }
      else if (ev.kind === 'dm.event' && ev.body.kind === 'attack.ac') { const body = ev.body; setLog((cur) => foldAttackAc(cur, body)); }
      // A Karma use (issue 1529): the test as the server folded it.
      else if (ev.kind === 'dm.event' && ev.body.kind === 'test.karma' && ev.body.record?.test) { const { rollId, record } = ev.body; setLog((cur) => cur.map((e) => (e.kind === 'roll' && e.roll.id === rollId ? { kind: 'roll', roll: record } : e))); }
      // A roll applied to tokens, or one application settled (qa-runs#406): the roll as the server stamped it.
      else if (ev.kind === 'dm.event' && (ev.body.kind === 'roll.apply' || ev.body.kind === 'roll.settle') && ev.body.record) { const { rollId, record } = ev.body; setLog((cur) => cur.map((e) => (e.kind === 'roll' && e.roll.id === rollId ? { kind: 'roll', roll: record } : e))); }
      else if (ev.kind === 'dm.event' && ev.body.kind === 'roll.amend') { const { rollId, total } = ev.body; const at = ev.at; setLog((cur) => cur.map((e) => (e.kind === 'roll' && e.roll.id === rollId ? { kind: 'roll', roll: { ...e.roll, fudged: { total, at } } } : e))); }
      // A player is told, briefly, when a token becomes theirs or stops being theirs (design/13 §4; slice 10).
      if (ev.kind === 'object.change' && 'mine' in ev.object) {
        const was = before.state.objects[ev.object.id];
        if (was && 'mine' in was && was.mine !== ev.object.mine) setNotice(ev.object.mine ? `${ev.object.name} is yours now.` : `${was.name ?? ev.object.name} is no longer yours.`);
      } else if (ev.kind === 'object.appear' && 'mine' in ev.object && ev.object.mine) setNotice(`${ev.object.name} is yours.`);
      // The turn order (slice 12): a player is told when a turn of theirs comes.
      const encWas = before.state.encounter as { active?: string | null; combatants?: Array<{ id: string; mine: boolean; name: string }> } | undefined;
      const encNow = r.store.state.encounter as { active?: string | null; combatants?: Array<{ id: string; mine: boolean; name: string }> } | undefined;
      // Issue 1050: from this player's own projection alone, so only the active player is ever nudged.
      const yours = role !== 'dm' ? turnCame(encWas, encNow) : null;
      if (yours) {
        setNotice(`It's your turn — ${yours}.`); nudgeTurn(yours);
        // The token whose turn it is comes selected (qa-runs 1253): a player with two isn't left on the other.
        const acting = (encNow as { combatants?: Array<{ id: string; tokens?: string[] }> } | undefined)?.combatants?.find((c) => c.id === encNow?.active);
        const token = acting?.tokens?.find((t) => r.store.state.objects[t]);
        if (token) selection.only(token);
      }
      // qa-runs 405: the DM's call for initiative reaches only the players it asks, each with only their own prompt.
      const called = role !== 'dm' && !yours ? promptCame(encWas as Parameters<typeof promptCame>[0], encNow as Parameters<typeof promptCame>[0]) : null;
      if (called) { setNotice(`The DM calls for initiative: roll for ${called.join(' and ')}.`); nudgeInitiative(called); }
      // Free move switched (qa-runs#1161): a player is told; the DM switched it.
      if (role !== 'dm' && ev.kind === 'scene.change') { const note = freeMoveNotice(before.state.scene, ev.scene); if (note) setNotice(note); }
      if (ev.kind === 'object.change' && ev.route && ev.route.length > 1) setTravel({ moves: [{ id: ev.object.id, route: ev.route }], n: r.store.n });
      else if (ev.kind === 'objects.change' && ev.routes) {
        const moves = Object.entries(ev.routes).filter(([, route]) => route.length > 1).map(([id, route]) => ({ id, route }));
        if (moves.length) setTravel({ moves, n: r.store.n });
      } else if (ev.kind === 'dm.event' && (ev.body.kind === 'object.move' || ev.body.kind === 'objects.move')) {
        // The DM reduces the body; its followers (issue 1031) travel with it, read from the state before it.
        const led = ev.body.kind === 'object.move' ? [{ id: ev.body.id, route: ev.body.route }] : ev.body.moves;
        const moves = [...led, ...followMoves(before.state as unknown as TableState, led)].filter((m) => m.route.length > 1).map((m) => ({ id: m.id, route: m.route }));
        if (moves.length) setTravel({ moves, n: r.store.n });
      }
    };
    const onStatus = (raw: unknown) => { const s = parseStatus(raw); if (s) setStatus(s); };
    // The scene changed under this person (issue 834): a player moved to the new live scene, or a DM whose staged
    // scene was deleted. A fresh snapshot, the counter reset; a player is told only that the scene changed.
    const onSwitch = (raw: unknown) => {
      const msg = raw as { campaignId?: unknown; snapshot?: Snapshot; resync?: unknown } | null;
      if (!msg || msg.campaignId !== campaignId || !msg.snapshot || !('state' in msg.snapshot)) return;
      const next = fromSnapshot(msg.snapshot.state, msg.snapshot.n);
      storeRef.current = next;
      setStore(next);
      setScene(msg.snapshot.scene ?? null);
      takeRules(msg.snapshot);
      takeAudio(msg.snapshot);
      // A resync (a change the server could not tell this socket, the review of 2026-10-10) is the same scene: nothing to say.
      if (role !== 'dm' && msg.resync !== true) setNotice('The scene changed.');
      refetchPeek();
    };
    // The live scene moved while this DM window is on another (issue 840): only "view as a player" reads the live
    // scene from here, so the nudge re-reads it while viewing as someone, and is otherwise nothing.
    const onLiveChanged = (raw: unknown) => {
      if ((raw as { campaignId?: unknown } | null)?.campaignId !== campaignId) return;
      if (peekRef.current) refetchPeek();
    };
    // The DM borrowed another system's combat rules, or gave them back (issue 1305): the rules this table now plays by.
    const onRules = (raw: unknown) => {
      const msg = raw as { campaignId?: unknown; systemRules?: unknown } | null;
      if (msg?.campaignId === campaignId) setSystemRules(parseRulesView(msg.systemRules));
    };
    // A notice for this person alone (`table:notice`): "The DM gave you Rope." Words, shown as any notice.
    const onSheet = (raw: unknown) => {
      const f = raw as { campaignId?: unknown; token?: unknown } | null;
      if (f && f.campaignId === campaignId && typeof f.token === 'string') sheetPulse.bump(f.token);
    };
    const onNotice = (raw: unknown) => {
      const msg = raw as { campaignId?: unknown; text?: unknown; offer?: unknown; unlink?: unknown } | null;
      if (msg?.campaignId !== campaignId || typeof msg.text !== 'string' || !msg.text) return;
      // Links the table made by name (qa-runs#1226), told to whoever runs the turn order: Unlink takes back those still
      // standing, read as it is pressed. The tokens and the rows stay.
      if (Array.isArray(msg.unlink)) {
        const pairs = msg.unlink as LinkPair[];
        setNotice(msg.text.slice(0, 300), { label: 'Unlink', run: () => {
          for (const p of pairs) {
            const c = storeRef.current.state.encounter?.combatants.find((x) => x.id === p.row) as Combatant | undefined;
            if (c?.tokens.includes(p.token)) actRef.current?.({ kind: 'encounter.link', id: c.id, tokens: c.tokens.filter((t) => t !== p.token) });
          }
        } });
        return;
      }
      // A failed maneuver's try-back (qa-runs#398): a button that sends it the other way round.
      const offer = msg.offer === undefined ? null : parseManeuverOffer(msg.offer);
      if (!offer) { setNotice(msg.text.slice(0, 300)); return; }
      setNotice(msg.text.slice(0, 300), { label: offer.label, run: () => actRef.current?.(offer.ask as never, (r) => { if (r.ok && r.notice) setNotice(r.notice); else if (!r.ok) setNotice(r.reason ?? 'That could not be done just now.'); }) });
    };
    socket.on(TABLE_RULES, onRules);
    socket.on(TABLE_EVENT, onEvent);
    socket.on(TABLE_STATUS, onStatus);
    socket.on(TABLE_SWITCH, onSwitch);
    socket.on(TABLE_LIVE_CHANGED, onLiveChanged);
    socket.on(TABLE_NOTICE, onNotice);
    socket.on(TABLE_SHEET, onSheet);
    return () => { socket.off(TABLE_SHEET, onSheet); socket.off(TABLE_RULES, onRules); socket.off(TABLE_EVENT, onEvent); socket.off(TABLE_STATUS, onStatus); socket.off(TABLE_SWITCH, onSwitch); socket.off(TABLE_LIVE_CHANGED, onLiveChanged); socket.off(TABLE_NOTICE, onNotice); };
  }, [socket, role, meId, resync, refetchPeek, campaignId, takeRules, takeAudio, loadLog]);

  // Only the latest ask's answer is taken (the review of 2026-10-10): two quick Views answered out of order never leave
  // this screen on the first; the server, too, answers an overtaken ask `superseded`.
  const viewAskedRef = useRef(0);
  const viewScene = useCallback((sceneId: string) => {
    const asked = ++viewAskedRef.current;
    socket.emit(TABLE_VIEW_SCENE, { campaignId, sceneId }, (snap: Snapshot | { error: string }) => {
      if (asked !== viewAskedRef.current) return;
      if (!('state' in snap)) { if (snap.error !== 'superseded') setNotice('That scene could not be opened.'); return; }
      const next = fromSnapshot(snap.state, snap.n);
      storeRef.current = next;
      setStore(next);
      setScene(snap.scene ?? null);
    });
  }, [socket, campaignId]);

  // A handover while looking through somebody's eyes: back to one's own.
  useEffect(() => { if (role !== 'dm' && peekRef.current) viewAs(null); }, [role, viewAs]);

  const act = useCallback((body: EventBody, onReply?: (reply: ActReply) => void) => {
    if (peekRef.current) {
      setNotice(lookingThrough(peekRef.current));
      onReply?.({ ok: false, error: 'refused', reason: 'viewing as' });
      return;
    }
    // The socket's own flag as well as the prop, which follows it a render behind: an emit on a socket that is not
    // connected is buffered by socket.io and sent on the reconnect, an act arriving long after the person was told.
    if (!connected || socket.connected === false) { setNotice(ACT_DISCONNECTED); onReply?.({ ok: false, error: 'refused', reason: ACT_DISCONNECTED }); return; }
    // A watcher acts on nothing (issue 1040): said here, never sent; the server refuses it as well.
    if (role === 'watcher') { setNotice('You are watching this table.'); onReply?.({ ok: false, error: 'forbidden' }); return; }
    setNotice('');
    // A move ends any offer of a floor, and its own reply may make a new one (qa-runs#1240): drag, arrow key or panel alike.
    if (body.kind === 'object.move') {
      floorOffer.set(null);
      const sent = onReply;
      onReply = (r) => { if (r.ok && r.floorOffer) floorOffer.set(r.floorOffer); sent?.(r); };
    }
    // Floors (qa-runs#1160; design/32 §2): a new thing, area or wall goes on the floor this person is viewing.
    body = onViewedFloor(body, storeRef.current.state as never, meId, role === 'dm');
    noteSent(body);
    // One answer per act: the table's, or the timeout's. A late reply is dropped (the act's event, if it landed, still
    // reaches the board as every event does), and a packet still in socket.io's buffer when time runs out is taken out
    // of it, so it is never sent on the reconnect after the person was told to try again.
    const payload = { campaignId, body };
    let answered = false;
    const timer = setTimeout(() => {
      answered = true;
      const buffer = (socket as { sendBuffer?: { data?: unknown[] }[] }).sendBuffer;
      if (Array.isArray(buffer)) { const i = buffer.findIndex((p) => p.data?.[1] === payload); if (i >= 0) buffer.splice(i, 1); }
      setNotice(ACT_TIMED_OUT);
      onReply?.({ ok: false, error: 'refused', reason: ACT_TIMED_OUT });
    }, ACT_TIMEOUT_MS);
    socket.emit(TABLE_ACT, payload, (reply: ActReply) => {
      if (answered) return;
      answered = true;
      clearTimeout(timer);
      onReply?.(reply);
      // What the act's follow-up met (issue 1052): hit points that could not reach the player's sheet, in words.
      if (reply.ok) { trackAct(body); if (reply.notice) setNotice(reply.notice); return; }
      if (reply.error === 'view_only') setNotice(VIEW_ONLY_REASON);
      // A warned attack: the button sends it again, the warning read.
      else if (reply.error === 'refused' && reply.anyway) setNotice(reply.reason ?? '', { label: reply.anyway, run: () => actRef.current?.({ ...(body as object), anyway: true } as never, onReply) });
      // The rare late refusal (design/20 §4.3): every move is checked on the screen before it is sent, so a move refused
      // for the turn is one whose turn ended while it was under way. The token has already gone back.
      else if (reply.error === 'refused' && body.kind === 'object.move' && reply.reason === NOT_YOUR_TURN) setNotice(TURN_ENDED);
      else if (reply.error === 'refused') setNotice(reply.reason ?? 'That did not stand.');
      else if (reply.error === 'forbidden') setNotice("That isn't yours to do.");
      // The DM is told the server's words for any other refusal (qa-runs 1432); a player keeps the plain line.
      else setNotice(role === 'dm' && reply.reason ? reply.reason : 'That could not be done.');
    });
  }, [connected, socket, campaignId, role]);
  actRef.current = act;

  // A quick ping (issue 1039): sent as it is, never while looking through somebody's eyes; the ones that arrive are the
  // board's to draw, over the bus.
  const ping = useCallback((x: number, y: number) => {
    if (peekRef.current || !connected || role === 'watcher') return;
    socket.emit(TABLE_PING, { campaignId, x, y }, () => {});
  }, [connected, socket, campaignId, role]);
  useEffect(() => {
    const onPing = (raw: unknown) => {
      const msg = raw as { campaignId?: unknown; ping?: Ping } | null;
      if (msg?.campaignId !== campaignId || !msg.ping || !Number.isFinite(msg.ping.x) || !Number.isFinite(msg.ping.y)) return;
      boardBus.pinged(msg.ping);
    };
    socket.on(TABLE_PING, onPing);
    return () => { socket.off(TABLE_PING, onPing); };
  }, [socket, campaignId]);

  // View as… (issue 1288): the log as that person is sent it — each entry through the same projection the server sends
  // a player (`projectEvent`): no private roll of the DM's, a player's version of every attack, no band, no fudge.
  const seenLog = useMemo(() => {
    if (!viewingAs) return log;
    const viewer = { id: viewingAs.id, role: 'player' as const };
    const empty = emptyState();
    return log.flatMap((e): LogEntry[] => {
      // A rule-check line is the DM's alone (issue 1131): a player is never sent one (the review of 2026-10-18).
      if (e.kind === 'note' && e.rule) return [];
      if (e.kind !== 'roll') return [e];
      const ev = projectEvent({ kind: 'roll', roll: e.roll }, empty, empty, viewer, null, e.roll.at);
      // The rewind's mark goes with the entry, as the player's own log carries it.
      const mark = e.rewound ? { rewound: true as const } : {};
      if (ev?.kind === 'roll') return [{ kind: 'roll', roll: ev.roll, ...mark }];
      if (ev?.kind === 'attack') return [{ kind: 'attack', attack: ev.attack, ...mark }];
      if (ev?.kind === 'save') return [{ kind: 'save', save: ev.save, ...mark }];
      if (ev?.kind === 'trigger') return [{ kind: 'trigger', trigger: ev.trigger, ...mark }];
      return [];
    });
  }, [log, viewingAs]);

  const value = useMemo<TableStateValue>(() => ({
    store: viewingAs ? (peek ?? emptyStore()) : store,
    status, notice, noticeId: noticeAt.id, noticeOpen: noticeAt.text ? noticeAt.open ?? null : null, connected,
    role: viewingAs ? viewingAs.role : role,
    powers: viewingAs ? null : powers,
    meId: viewingAs ? viewingAs.id : meId,
    travel: viewingAs ? null : travel,
    act, setNotice, viewingAs, viewAs, log: seenLog, logLoads, logMore, loadOlderLog, asks: viewingAs ? {} : asks, rules, rulings, handouts: viewingAs ? handoutsAs(handouts, viewingAs.id) : handouts, decks: viewingAs ? {} : decks, audio, clockSkew, systemRules,
    scene: viewingAs ? null : scene, viewScene, ping,
  }), [store, peek, status, notice, noticeAt.id, logLoads, connected, role, meId, travel, act, viewingAs, viewAs, log, logMore, loadOlderLog, asks, rules, rulings, handouts, decks, audio, clockSkew, systemRules, scene, viewScene, ping, seenLog, powers]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** What a player is told when Free move changes (qa-runs#1161), or null when it did not. */
export function freeMoveNotice(was: { freeMove?: true } | undefined, now: { freeMove?: true }): string | null {
  if ((was?.freeMove === true) === (now.freeMove === true)) return null;
  return now.freeMove ? 'Free move is on: tokens move without the area or a confirmation.' : 'Free move is off: pick the kind of move and confirm each one.';
}
